/**
 * Where each item field is kept, and which way it moves (canon 01 › Field Source, Sync Direction
 * and Conflict Rule). Read from what already decides it: the item's links (entity_external_ids),
 * each connected system's trust rules and sync direction (connector_configs), and the fields each
 * system can carry today (INBOUND_TRANSPORT / OUTBOUND_TRANSPORT).
 *
 * A field crosses only when all three agree. A rule for a field the system cannot carry, or a
 * direction the connection is not set to, leaves the field kept here.
 *
 * The rule for each field is the connector's Field Sync Rule (supabase/functions/_shared/
 * fieldSyncRules.ts, the copy `dispatch` and `apply` use too). A scoring attribute (Impact,
 * Effort, …) with no rule of its own takes its direction from the connector's Field Mappings
 * rule for it; it goes out where that rule names a field the engine can write.
 */
import type { ConnectorConfigDB } from './connectors';
import { INBOUND_TRANSPORT, OUTBOUND_TRANSPORT } from './semanticConnectors/unifiedAttributes';
import {
  CONFLICT_WORDS,
  comesIn,
  editableHere,
  fieldSyncRuleFor,
  readStoredRule,
  ruleFromMappingDirection,
  sendsOut,
  type FieldSyncRule,
  type StoredFieldRules,
} from '../../supabase/functions/_shared/fieldSyncRules';
import { ENGINE_WRITABLE_FIELDS } from '../../supabase/functions/_shared/fieldSyncOutbound';
import { parseFieldMappingConfig } from './fieldMappingRegistry';
import { attributeKeyOfTarget, externalFieldLabel, resolveActiveMappingSet } from './fieldMappingApply';

/** The item fields any connected system can carry today. */
export type SyncedItemField = 'title' | 'description' | 'tags';
export const SYNCED_ITEM_FIELDS: readonly SyncedItemField[] = ['title', 'description', 'tags'];

export const SYNCED_FIELD_LABELS: Record<SyncedItemField, string> = {
  title: 'Title',
  description: 'Description',
  tags: 'Tags',
};

/**
 * The names a rule for this field may be saved under: ours first (the name the built-in
 * defaults use), then the name the other system sends (Jira's summary and labels).
 */
const RULE_KEYS: Record<SyncedItemField, string[]> = {
  title: ['title', 'summary'],
  description: ['description'],
  tags: ['tags', 'labels'],
};

export interface ItemLinkInput {
  connectorType: string;
  externalId: string;
  externalUrl: string | null;
  /** When the link last synced; of two links to one system, the more recent one is the item's. */
  lastSyncedAt?: string | null;
}

/** One field, one linked system. An attribute's field is `attribute:<key>`. */
export interface FieldSource {
  field: SyncedItemField | `attribute:${string}`;
  connectorType: string;
  systemName: string;
  /** there: kept in the other system. here: kept in Priorities.ai and copied out. shared: either side may change it. */
  keptIn: 'there' | 'here' | 'shared';
  direction: 'in' | 'out';
  /** False when the field is kept in the other system and nothing is sent back. */
  editableHere: boolean;
  externalUrl: string | null;
  /** One plain sentence: who keeps it, which way it moves. */
  sentence: string;
}

export interface ItemFieldSources {
  /** Every field that crosses, per field. A field with no entry is kept in Priorities.ai only. */
  byField: Partial<Record<SyncedItemField, FieldSource[]>>;
  /** Every scoring attribute that crosses, by attribute key (from each connector's import rules). */
  byAttribute: Record<string, FieldSource[]>;
  /** The linked systems that are connected and carry at least one field. */
  systems: string[];
}

/** The connector's rule for one field, whichever of its names the rule was saved under. */
export function ruleForSyncedField(rules: StoredFieldRules | null | undefined, field: SyncedItemField): FieldSyncRule {
  const keys = RULE_KEYS[field];
  const saved = keys.map((k) => readStoredRule(rules?.[k])).find(Boolean) ?? null;
  return fieldSyncRuleFor(rules, keys[0], saved);
}

/** "When both sides change it, Priorities.ai's value stands." */
function conflictSentence(rule: FieldSyncRule, name: string): string {
  if (rule.conflict === 'person_decides') return 'When both sides change it, a person decides in the Conflict Queue.';
  if (rule.conflict === 'most_recent_wins') return 'When both sides change it, the more recent change stands.';
  return `When both sides change it, ${rule.owner === 'external' ? name : 'Priorities.ai'}'s value stands (${CONFLICT_WORDS.owner_wins.toLowerCase()}).`;
}

/** A workspace-shared connection with credentials. The sync functions read only these. */
export function isSyncingConnection(c: ConnectorConfigDB): boolean {
  return c.userId === null && c.isActive && Object.keys(c.authData ?? {}).length > 0;
}

export function resolveItemFieldSources(
  links: ItemLinkInput[],
  configs: ConnectorConfigDB[],
  systemName: (connectorType: string) => string,
): ItemFieldSources {
  const byField: ItemFieldSources['byField'] = {};
  const systems = new Set<string>();
  const byAttribute: ItemFieldSources['byAttribute'] = {};
  const seen = new Set<string>();
  const newestFirst = [...links].sort((a, b) => (b.lastSyncedAt ?? '').localeCompare(a.lastSyncedAt ?? ''));

  for (const link of newestFirst) {
    if (seen.has(link.connectorType)) continue;
    seen.add(link.connectorType);
    const config = configs.find((c) => c.connectorType === link.connectorType && isSyncingConnection(c));
    if (!config) continue;
    const name = systemName(link.connectorType);
    const direction = config.syncDirection ?? 'both';

    for (const field of SYNCED_ITEM_FIELDS) {
      const rule = ruleForSyncedField(config.trustRules as StoredFieldRules, field);
      const carriedIn = comesIn(rule, direction) && (INBOUND_TRANSPORT[link.connectorType]?.has(field) ?? false);
      const carriedOut = sendsOut(rule, direction) && (OUTBOUND_TRANSPORT[link.connectorType]?.has(field) ?? false);
      const label = SYNCED_FIELD_LABELS[field].toLowerCase();
      const source = sourceFor(field, rule, carriedIn, carriedOut, link, name, `The ${label}`, field === 'tags' ? 'are' : 'is');
      if (source) {
        (byField[field] ??= []).push(source);
        systems.add(name);
      }
    }

    for (const source of attributeSourcesOf(link, config, name)) {
      const key = source.field.slice('attribute:'.length);
      if ((byAttribute[key] ??= []).some((s) => s.connectorType === source.connectorType)) continue;
      byAttribute[key].push(source);
      systems.add(name);
    }
  }
  return { byField, byAttribute, systems: [...systems] };
}

/** One field's mark for one linked system, or null when nothing crosses. */
function sourceFor(
  field: FieldSource['field'],
  rule: FieldSyncRule,
  carriedIn: boolean,
  carriedOut: boolean,
  link: ItemLinkInput,
  name: string,
  subject: string,
  verb: 'is' | 'are' = 'is',
): FieldSource | null {
  const base = { field, connectorType: link.connectorType, systemName: name, externalUrl: link.externalUrl, editableHere: editableHere(rule) };
  if (rule.direction === 'in' && carriedIn) {
    return { ...base, keptIn: 'there', direction: 'in', sentence: `${subject} ${verb} kept in ${name} and copied here. Change it in ${name}.` };
  }
  if (rule.direction === 'out' && carriedOut) {
    return {
      ...base, keptIn: 'here', direction: 'out',
      sentence: `${subject} ${verb} kept here and copied to ${name}. A change made in ${name} is not brought back; it shows as a difference.`,
    };
  }
  if (rule.direction === 'both' && (carriedIn || carriedOut)) {
    const ways = carriedIn && carriedOut
      ? `Changes on either side are copied to the other. ${conflictSentence(rule, name)}`
      : carriedIn
        ? `Changes made in ${name} come here; ${name} cannot take changes made here yet.`
        : `Changes made here go to ${name}; changes made in ${name} do not come here yet.`;
    return { ...base, keptIn: 'shared', direction: carriedIn ? 'in' : 'out', sentence: `${subject} ${verb} shared with ${name}. ${ways}` };
  }
  return null;
}

/** The attributes a connection's Field Mappings rules carry, for one linked record. */
function attributeSourcesOf(link: ItemLinkInput, config: ConnectorConfigDB, name: string): FieldSource[] {
  const { set } = resolveActiveMappingSet(parseFieldMappingConfig(config.fieldMappings), 'import');
  const direction = config.syncDirection ?? 'both';
  const out: FieldSource[] = [];
  for (const entry of set?.entries ?? []) {
    if (!entry.enabled || !entry.externalFieldKey) continue;
    const attributeKey = attributeKeyOfTarget(entry.internalFieldKey);
    if (!attributeKey) continue;
    const field = `attribute:${attributeKey}` as const;
    const rule = fieldSyncRuleFor(config.trustRules as StoredFieldRules, field, ruleFromMappingDirection(entry.direction));
    const carriedIn = comesIn(rule, direction);
    const carriedOut = sendsOut(rule, direction) && (ENGINE_WRITABLE_FIELDS[link.connectorType]?.has(entry.externalFieldKey) ?? false);
    const source = sourceFor(field, rule, carriedIn, carriedOut, link, name, `This value (${externalFieldLabel(link.connectorType, entry)})`);
    if (source) out.push(source);
  }
  return out;
}

/**
 * Which connected systems keep each scoring attribute for the whole workspace, before any
 * item is linked: every syncing connection's Field Mappings rules, read the way an item's
 * are (attributeSourcesOf) with no record behind them. The track's Criteria & scales column
 * and the session designer's S4 read this to say "the record, kept by Jira" for a criterion
 * a connector is the source of (where a criterion's values come from, migration 1062).
 */
export function resolveWorkspaceAttributeSources(
  configs: ConnectorConfigDB[],
  systemName: (connectorType: string) => string,
): Record<string, FieldSource[]> {
  const byAttribute: Record<string, FieldSource[]> = {};
  for (const config of configs) {
    if (!isSyncingConnection(config)) continue;
    const link: ItemLinkInput = { connectorType: config.connectorType, externalId: '', externalUrl: null };
    for (const source of attributeSourcesOf(link, config, systemName(config.connectorType))) {
      const key = source.field.slice('attribute:'.length);
      if ((byAttribute[key] ??= []).some((s) => s.connectorType === source.connectorType)) continue;
      byAttribute[key].push(source);
    }
  }
  return byAttribute;
}

/**
 * The system that keeps this attribute's value, if one does: kept there, or shared so that
 * system may change it. A value only copied out ("here") is still kept in Priorities.ai.
 */
export function keeperOf(sources: FieldSource[] | undefined): FieldSource | null {
  return sources?.find((s) => s.keptIn === 'there') ?? sources?.find((s) => s.keptIn === 'shared') ?? null;
}

/** The short words for a mark: "from Jira", "to Jira", "shared with Jira". */
export function sourceWords(s: Pick<FieldSource, 'keptIn' | 'systemName'>): string {
  return s.keptIn === 'there' ? `from ${s.systemName}` : s.keptIn === 'here' ? `to ${s.systemName}` : `shared with ${s.systemName}`;
}

/** The first system that keeps this field and sends nothing back — the one to change it in. */
export function keptElsewhere(sources: FieldSource[] | undefined): FieldSource | null {
  return sources?.find((s) => !s.editableHere) ?? null;
}
